import type { TypedDocumentNode, FetchResult } from '@apollo/client';
import { gql } from '@apollo/client';

import type { SignupChallengeStep } from '@jerry-serverless/enums/user_challenge';

import { getClient } from '../index';
import { getCognitoAuthCredentials } from '../../amplify/auth';

import type { SignupUserInput } from './signup.mutation';
import type { UpdateUserData, UpdateUserInput } from './update_user.mutation';
import { SignupStep, UPDATE_USER_MUTATION } from './update_user.mutation';
import type { User } from './user.fragment';
import { USER_FRAGMENT } from './user.fragment';
import type { UpdatePolicyInfoInput } from './update_policy_info.mutation';

interface FinishLoginChallengeOutput {
  finishLoginChallenge: { user: User | null; signupChallengeStep?: SignupChallengeStep };
}

const FINISH_LOGIN_CHALLENGE_MUTATION: TypedDocumentNode<FinishLoginChallengeOutput, Partial<SignupUserInput>> = gql`
  mutation FinishLoginChallenge($input: SignupUser4Input) {
    finishLoginChallenge(input: $input) {
      user {
        ${USER_FRAGMENT}
      }
      signupChallengeStep
    }
  }
`;

interface FinishLoginChallengeWithCreateNewAccountOutput {
  finishLoginChallengeWithCreateNewAccount: { user: User | null; signupChallengeStep?: SignupChallengeStep };
}

const FINISH_LOGIN_CHALLENGE_WITH_NEW_ACCOUNT_MUTATION: TypedDocumentNode<
  FinishLoginChallengeWithCreateNewAccountOutput,
  SignupUserInput
> = gql`
  mutation FinishLoginChallengeWithCreateNewAccount($input: SignupUser4Input!) {
    finishLoginChallengeWithCreateNewAccount(input: $input) {
      user {
        ${USER_FRAGMENT}
      }
      signupChallengeStep
    }
  }
`;

interface FinishDobChallengeOutput {
  finishDobChallenge: { user: User | null; signupChallengeStep?: SignupChallengeStep };
}

const FINISH_LOGIN_CHALLENGE_WITH_DOB_VERIFICATION_MUTATION: TypedDocumentNode<
  FinishDobChallengeOutput,
  { dob: string } & Partial<SignupUserInput>
> = gql`
  mutation finishDobChallenge($dob: String!) {
    finishDobChallenge(dob: $dob) {
      user {
        ${USER_FRAGMENT}
      }
      signupChallengeStep
    }
  }
`;

/**
 * Finish login challenge
 */
export const finishLoginChallenge = async (input?: SignupUserInput['input']): Promise<FinishLoginChallengeOutput> => {
  const client = getClient();
  const result = await client.mutate({
    mutation: FINISH_LOGIN_CHALLENGE_MUTATION,
    variables: { input },
    fetchPolicy: 'network-only',
  });

  if (result.errors?.length) {
    throw new Error(result.errors[0].message);
  }

  if (!result.data) {
    throw new Error('No data returned');
  }

  return result.data;
};

/**
 * Finish login challenge with create new account
 */
export const finishLoginChallengeWithCreateNewAccount = async (
  input: SignupUserInput['input']
): Promise<FinishLoginChallengeWithCreateNewAccountOutput> => {
  const client = getClient();
  const result = await client.mutate({
    mutation: FINISH_LOGIN_CHALLENGE_WITH_NEW_ACCOUNT_MUTATION,
    variables: { input },
    fetchPolicy: 'network-only',
  });

  if (result.errors?.length) {
    throw new Error(result.errors[0].message);
  }

  if (!result.data) {
    throw new Error('No data returned');
  }

  const { user, signupChallengeStep } = result.data.finishLoginChallengeWithCreateNewAccount;

  const reqs: [Promise<FetchResult<UpdateUserData>>, Promise<void>?] = [
    client.mutate<UpdateUserData, UpdateUserInput>({
      mutation: UPDATE_USER_MUTATION,
      variables: {
        input: {
          name: user?.name,
          signature: user?.name,
          step: SignupStep.verify,
        },
      },
    }),
  ];
  // update Cognito credentials after creating a new user account
  if (input.phone) {
    // must wait for the cognito tokens retrieval, otherwise the relevant requests would be canceled due to browser page reload
    reqs.push(getCognitoAuthCredentials({ phone: input.phone }));
  }
  // NOTE: we make the update user request and cognito token request in parallel to decrease the time of the whole process,
  // but we need to pay attention to ensure the cognito token retrieval is completely independent of the update user request,
  // i.e. the cognito token can't include the user attributes that are updated in the update user request.
  const [updateUserResp] = await Promise.all(reqs);
  if (updateUserResp.errors?.length) {
    throw new Error(updateUserResp.errors[0].message);
  }
  if (!updateUserResp.data) {
    throw new Error('Update user failed');
  }

  return {
    finishLoginChallengeWithCreateNewAccount: { user: updateUserResp.data.updateUser5.user, signupChallengeStep },
  };
};

/**
 * Finish dob challenge
 */
export const finishDobChallenge = async (dob: string): Promise<FinishDobChallengeOutput> => {
  const client = getClient();
  const result = await client.mutate({
    mutation: FINISH_LOGIN_CHALLENGE_WITH_DOB_VERIFICATION_MUTATION,
    variables: { dob },
    fetchPolicy: 'network-only',
  });

  if (result.errors?.length) {
    throw new Error(result.errors[0].message);
  }

  if (!result.data) {
    throw new Error('No data returned');
  }

  return result.data;
};

export const FINISH_UPDATE_DOB_CHALLENGE_MUTATION: TypedDocumentNode<
  {
    finishUpdateDobChallenge: {
      success: boolean;
      remainingAttempts: number;
      signupChallengeStep?: SignupChallengeStep;
    };
  },
  {
    input: {
      policyCarrier: string;
      car: string;
      dateOfBirth: string;
    };
  }
> = gql`
  mutation FinishUpdateDobChallenge($input: FinishUpdateDobChallengeInput!) {
    finishUpdateDobChallenge(input: $input) {
      success
      remainingAttempts
      signupChallengeStep
    }
  }
`;

export interface ConfirmInfoMutationInput
  extends Pick<
      SignupUserInput['input'],
      | 'isAutoInsured'
      | 'autoInsuredLength'
      | 'notInsuredReason'
      | 'claims'
      | 'violations'
      | 'violationsNotSure'
      | 'isHomeowner'
      | 'homeOwnership'
      | 'creditRange'
      | 'address'
    >,
    Partial<Pick<UpdatePolicyInfoInput, 'cars' | 'drivers'>> {
  email?: string;
}

/**
 *  A subset of signupParams fields that are used by mutation ConfirmInfo
 */
export const ConfirmInfoSignupParamsFields = [
  'isAutoInsured',
  'autoInsuredLength',
  'notInsuredReason',
  'claims',
  'violations',
  'violationsNotSure',
  'homeOwnership',
  'creditRange',

  'addressBusiness',
  'addressDescription',
] as const;

export const CONFIRM_INFO_MUTATION: TypedDocumentNode<unknown, { input: ConfirmInfoMutationInput }> = gql`
  mutation ConfirmInfo($input: ConfirmInfoInputType!) {
    confirmInfo(input: $input) {
      user {
        id
      }
    }
  }
`;

export const confirmInfoSubmit = async (input: ConfirmInfoMutationInput) => {
  const client = getClient();

  const result = await client.mutate({
    mutation: CONFIRM_INFO_MUTATION,
    variables: { input },
    fetchPolicy: 'network-only',
  });

  if (result.errors?.length) {
    throw new Error(result.errors[0].message);
  }

  if (!result.data) {
    throw new Error('No data returned');
  }

  return result.data;
};
